import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, CalendarX2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/Shell";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const TimeOffTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.timeOff()); } catch { toast.error("Failed to load requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    try { await api.updateTimeOff(id, status); toast.success(`Request ${status}`); load(); }
    catch { toast.error("Could not update request"); }
  };
  const remove = async (id) => {
    try { await api.deleteTimeOff(id); toast.success("Request removed"); load(); }
    catch { toast.error("Could not remove request"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <h2 className="font-display text-2xl font-bold mb-1">Time Off</h2>
      <p className="text-sm text-muted-foreground mb-6">Approve or decline crew leave requests.</p>
      {rows.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl">
          <CalendarX2 className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
          <p className="text-muted-foreground">No time-off requests.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`timeoff-${r.id}`} className="bg-card border border-border rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-semibold">{r.worker_name || r.worker_email}</p>
                  <StatusBadge status={r.status === "denied" ? "denied" : r.status} />
                </div>
                <p className="text-sm text-muted-foreground mt-1 font-mono">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</p>
                {r.reason && <p className="text-sm text-muted-foreground mt-1">{r.reason}</p>}
              </div>
              <div className="flex items-center gap-2">
                {r.status === "pending" && (
                  <>
                    <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "approved")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`deny-${r.id}`} onClick={() => decide(r.id, "denied")} className="rounded-full"><X className="w-4 h-4 mr-1" /> Decline</Button>
                  </>
                )}
                <Button size="icon" variant="ghost" data-testid={`remove-timeoff-${r.id}`} onClick={() => remove(r.id)} className="rounded-full text-destructive hover:bg-destructive/10 h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
